import type { FC, ReactNode } from 'react'

import { CheckCircle2, ScanLine, Ticket, TrendingUp } from 'lucide-react'
import { Logo } from '@/components/generals/logo'
import { DecorativeQr } from '@/components/generals/decorative-qr'

interface AuthShellProps {
    title: string
    description: string
    children: ReactNode
    footer?: ReactNode
}

const POINTS = [
    { icon: Ticket, text: 'Publish an event page with as many ticket types as you need' },
    { icon: ScanLine, text: 'Check guests in from any phone, no app required' },
    { icon: TrendingUp, text: 'Watch sales, revenue and check-ins update live' }
]

export const AuthShell: FC<AuthShellProps> = ({ title, description, children, footer }): ReactNode => {
    return (
        <div className='grid min-h-screen lg:grid-cols-[1fr_1.05fr]'>
            <main id='main-content' className='flex flex-col px-4 py-8 sm:px-10'>
                <Logo />

                <div className='mx-auto flex w-full max-w-md flex-1 flex-col justify-center gap-8 py-12'>
                    <div className='flex flex-col gap-2'>
                        <h1 className='text-3xl font-extrabold sm:text-4xl'>{title}</h1>
                        <p className='text-muted-foreground'>{description}</p>
                    </div>

                    {children}

                    {footer && <div className='text-sm text-muted-foreground'>{footer}</div>}
                </div>
            </main>

            <aside className='relative hidden overflow-hidden bg-ink p-12 text-ink-foreground lg:flex lg:flex-col lg:justify-between' aria-hidden='true'>
                <div className='bg-grid-ink absolute inset-0 [mask-image:radial-gradient(ellipse_at_top_right,black_25%,transparent_70%)]' />
                <div className='absolute -right-24 -top-24 size-[30rem] rounded-full bg-primary/50 blur-[130px]' />

                <div className='relative flex flex-col gap-6'>
                    <p className='font-heading text-4xl font-extrabold leading-tight text-white'>
                        Sell the tickets.<br />Run the door.<br /><span className='text-accent'>See every sale.</span>
                    </p>

                    <ul className='flex flex-col gap-4'>
                        {POINTS.map(point => (
                            <li key={point.text} className='flex items-center gap-3 text-ink-muted'>
                                <span className='flex size-9 items-center justify-center rounded-xl bg-white/10 text-accent'>
                                    <point.icon className='size-4' />
                                </span>
                                {point.text}
                            </li>
                        ))}
                    </ul>
                </div>

                <div className='relative flex items-center gap-5 self-start rounded-3xl bg-card p-5 text-foreground shadow-2xl'>
                    <div className='rounded-2xl border border-border bg-white p-2'>
                        <DecorativeQr seed={23} className='size-20 text-ink' />
                    </div>
                    <div className='flex flex-col gap-1'>
                        <span className='text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground'>Admit one</span>
                        <span className='font-mono text-sm font-semibold'>9QX2-HK47-ME3T</span>
                        <span className='inline-flex items-center gap-1 text-sm font-semibold text-success'>
                            <CheckCircle2 className='size-4' />
                            Checked in
                        </span>
                    </div>
                </div>
            </aside>
        </div>
    )
}

export const safeNext = (value: unknown): string => {
    if (typeof value !== 'string') return '/dashboard'
    if (!value.startsWith('/') || value.startsWith('//') || value.startsWith('/\\')) return '/dashboard'
    return value
}
